/**
 * Full-screen image lightbox with wheel zoom + drag pan (feature: doble-click
 * en imagen). Built on the existing Radix Dialog (ui/dialog.jsx) — no new
 * dependency. Esc / backdrop click / the built-in X all close it for free via
 * Radix; this component only adds the zoom/pan behaviour and +/-/reset
 * controls.
 */
import React, { useEffect, useRef, useState } from 'react';
import { ZoomIn, ZoomOut, RotateCcw, Download } from 'lucide-react';
import { Dialog, DialogContent } from '../ui/dialog';
import { isCanonicalAsset, resolveSignedAsset } from '../../lib/signedAssets';
import { downloadAsset } from '../../lib/downloadAsset';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.25;
const WHEEL_SENSITIVITY = 0.0015;

const clampZoom = (z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

const ImageLightbox = ({ open, onOpenChange, src, alt }) => {
  const [displaySrc, setDisplaySrc] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null); // { startX, startY, panX, panY } while dragging

  // Reset zoom/pan every time the lightbox opens (or the image changes), and
  // resolve the canonical /assets path to a signed URL (same pattern as
  // ImageNodeView) — external/data URLs pass through unchanged.
  useEffect(() => {
    if (!open) return;
    setZoom(1);
    setPan({ x: 0, y: 0 });
    let active = true;
    if (isCanonicalAsset(src)) {
      setDisplaySrc(null);
      resolveSignedAsset(src).then((url) => {
        if (active) setDisplaySrc(url || src);
      });
    } else {
      setDisplaySrc(src);
    }
    return () => { active = false; };
  }, [open, src]);

  const handleWheel = (e) => {
    e.preventDefault();
    setZoom((z) => {
      const next = clampZoom(z - e.deltaY * WHEEL_SENSITIVITY * z);
      if (next <= MIN_ZOOM) setPan({ x: 0, y: 0 });
      return next;
    });
  };

  const handleMouseDown = (e) => {
    if (zoom <= MIN_ZOOM) return;
    dragRef.current = { startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y };
  };

  const handleMouseMove = (e) => {
    if (!dragRef.current) return;
    const { startX, startY, panX, panY } = dragRef.current;
    setPan({ x: panX + (e.clientX - startX), y: panY + (e.clientY - startY) });
  };

  const stopDrag = () => { dragRef.current = null; };

  const zoomIn = () => setZoom((z) => clampZoom(z + ZOOM_STEP));
  const zoomOut = () => setZoom((z) => {
    const next = clampZoom(z - ZOOM_STEP);
    if (next <= MIN_ZOOM) setPan({ x: 0, y: 0 });
    return next;
  });
  const resetZoom = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  // Hallazgo del dueño (P36): el lightbox tenía zoom/rotación pero no descarga.
  // SIEMPRE via blob->objectURL: un <a download> con URL cross-origin (signed
  // CDN, legacy /api/images, externa) es IGNORADO por el navegador y NAVEGA a
  // la imagen (2ª ronda B3). Asset canónico -> descarga autenticada; cualquier
  // otra URL -> fetch a blob; si ni eso se puede, nueva pestaña (jamás navegar
  // el editor).
  const handleDownload = async () => {
    if (isCanonicalAsset(src)) {
      downloadAsset(src);
      return;
    }
    if (!displaySrc) return;
    const filename = (alt || 'imagen').replace(/\s+/g, '_');
    try {
      const resp = await fetch(displaySrc);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const objectUrl = URL.createObjectURL(await resp.blob());
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      window.open(displaySrc, '_blank', 'noopener');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-none w-screen h-screen inset-0 left-0 top-0 translate-x-0 translate-y-0 p-0 border-0 rounded-none bg-black/95 shadow-none flex items-center justify-center overflow-hidden [&>button]:text-white [&>button]:opacity-80 [&>button]:hover:opacity-100"
        onWheel={handleWheel}
      >
        <div
          className="w-full h-full flex items-center justify-center"
          style={{ cursor: zoom > MIN_ZOOM ? (dragRef.current ? 'grabbing' : 'grab') : 'default' }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={stopDrag}
          onMouseLeave={stopDrag}
        >
          {displaySrc && (
            <img
              src={displaySrc}
              alt={alt || ''}
              draggable={false}
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                maxWidth: '90vw',
                maxHeight: '90vh',
                userSelect: 'none',
                objectFit: 'contain',
              }}
            />
          )}
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-black/70 rounded-lg p-1.5">
          <button
            type="button"
            onClick={zoomOut}
            title="Alejar"
            className="p-2 rounded-md text-white/90 hover:bg-white/10 hover:text-white transition-colors"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={resetZoom}
            title="Restablecer zoom"
            className="px-2 py-1 rounded-md text-white/90 hover:bg-white/10 hover:text-white transition-colors text-xs font-medium tabular-nums min-w-[3.5rem]"
          >
            {Math.round(zoom * 100)}%
          </button>
          <button
            type="button"
            onClick={zoomIn}
            title="Acercar"
            className="p-2 rounded-md text-white/90 hover:bg-white/10 hover:text-white transition-colors"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={resetZoom}
            title="Reiniciar (1x)"
            className="p-2 rounded-md text-white/90 hover:bg-white/10 hover:text-white transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
          <div className="w-px h-5 bg-white/20 mx-0.5" />
          <button
            type="button"
            onClick={handleDownload}
            title="Descargar imagen"
            className="p-2 rounded-md text-white/90 hover:bg-white/10 hover:text-white transition-colors"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageLightbox;
